import { T } from "@repo/transifex";
import {
  ScreenErrorText,
  ScreenErrorTitle,
  ScreenError
} from "~/components/screen/screen-error";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import {
  ScreenSuccessTitle,
  ScreenSuccess,
  ScreenSuccessText
} from "~/components/screen/screen-success";
import { useCreateOrderSimple } from "~/api/orders/hooks/use-create-order-simple";
import { LayoutLock } from "~/components/layout/layout-lock";
import { usePrintOrderReceipt } from "~/hooks/queries/orders";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { logger } from "~/services/logger";
import { usePerformance } from "~/hooks/use-performance";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { useToast } from "~/components/hooks/use-toast";
import { useScanner } from "~/hooks/use-scanner";
import { CODE_FORMATS } from "~/hooks/use-scanner/code-formats";
import { ScanCardSvg } from "~/components/assets/scan-card-svg";

import { TicketScanSvg } from "../__app.ticket-validation.index/assets/TicketScanSvg";

const SPANS = {
  CREATE_ORDER: "create_order",
  PRINT_RECEIPT: "print_receipt"
} as const;

export const SimpleCheckoutPage = () => {
  const { start, end } = usePerformance();
  const { data: till } = useCurrentTill();

  const hasCardAuthentication = !(
    till?.type === "simple-checkout" &&
    till.config.authenticationMethod === "qr-code"
  );

  const { toast } = useToast();

  const { mutate: createOrder, status, reset, error } = useCreateOrderSimple();
  const { mutate: createReceipt } = usePrintOrderReceipt();

  const hasAutomaticReceiptPrint =
    till?.type === "simple-checkout" &&
    till.config.receiptPrintMode === "automatic";

  const operationName = hasAutomaticReceiptPrint
    ? "simple-checkout-automatic-receipt"
    : "simple-checkout";

  const handleScan = ({
    authenticationMethod,
    authenticationCode
  }: {
    authenticationMethod: "cardHex" | "qrCode";
    authenticationCode?: string;
  }) => {
    if (status !== "idle") {
      logger.debug("order creation is pending, skipping", {
        operation_name: operationName
      });
      return;
    }

    start(operationName);
    start(SPANS.CREATE_ORDER);

    logger.debug("scanned employee card", {
      operation_name: operationName,
      operation_status: "start",
      span_name: SPANS.CREATE_ORDER,
      span_status: "start"
    });

    logger.debug("call api to create simple order", {
      operation_name: operationName,
      span_name: SPANS.CREATE_ORDER
    });

    createOrder(
      {
        authenticationMethod,
        authenticationCode
      },
      {
        onSuccess: (order) => {
          logger.trace("create order success", {
            order_id: order.id,
            operation_name: operationName,
            span_name: SPANS.CREATE_ORDER,
            span_duration: end(SPANS.CREATE_ORDER),
            span_status: "success"
          });

          if (
            till?.type === "simple-checkout" &&
            till.config.receiptPrintMode === "automatic"
          ) {
            start(SPANS.PRINT_RECEIPT);
            logger.debug("call api to print receipt", {
              operation_name: operationName,
              span_name: SPANS.PRINT_RECEIPT,
              span_status: "start"
            });
            createReceipt(
              {
                orderId: order.id,
                userId: null,
                isTicket: till?.moduleConfig.hidePrices ?? false
              },
              {
                onSuccess: () => {
                  logger.debug("receipt sent to printer", {
                    operation_name: operationName,
                    operation_status: "success",
                    operation_duration: end(operationName),
                    span_name: SPANS.PRINT_RECEIPT,
                    span_duration: end(SPANS.PRINT_RECEIPT),
                    span_status: "success"
                  });
                },
                onError: (error) => {
                  logger.error(error, "error printing receipt", {
                    operation_name: operationName,
                    operation_status: "error",
                    operation_duration: end(operationName),
                    span_name: SPANS.PRINT_RECEIPT,
                    span_duration: end(SPANS.PRINT_RECEIPT),
                    span_status: "error"
                  });
                }
              }
            );
          }
        },
        onError: (error) => {
          logger.error(error, "error creating order", {
            operation_name: operationName,
            operation_duration: end(operationName),
            operation_status: "error",
            span_name: SPANS.CREATE_ORDER,
            span_duration: end(SPANS.CREATE_ORDER),
            span_status: "error"
          });
        }
      }
    );
  };

  useScanner({
    onSuccess: (result) => {
      switch (result.format) {
        case CODE_FORMATS.QR_CHILD_ID: {
          handleScan({
            authenticationMethod: "qrCode",
            authenticationCode: result.value
          });
          break;
        }

        case CODE_FORMATS.BARCODE_PRODUCT_OR_GENERIC:
        case CODE_FORMATS.GENERIC: {
          handleScan({
            authenticationMethod: "cardHex",
            authenticationCode: result.value
          });
          break;
        }

        default: {
          toast({
            title: <T _str="Scanning failed" />,
            description: <T _str="Code could not be handled" />,
            variant: "destructive"
          });
        }
      }
    }
  });

  if (status === "error") {
    return (
      <ScreenError onClose={reset} withAnimation={false}>
        <ScreenErrorTitle>
          <DisplayErrorMessage error={error} />
        </ScreenErrorTitle>

        <ScreenErrorText>
          <T _str="Please try again or ask staff for help" />
        </ScreenErrorText>
      </ScreenError>
    );
  }

  if (status === "success") {
    return (
      <ScreenSuccess onClose={reset}>
        <ScreenSuccessTitle>
          <T _str="Registration complete" />
        </ScreenSuccessTitle>

        <ScreenSuccessText>
          <T _str="Enjoy your meal!" />
        </ScreenSuccessText>
      </ScreenSuccess>
    );
  }

  if (status === "pending") {
    return (
      <LayoutLock>
        <LoadingSpinner />
      </LayoutLock>
    );
  }

  return (
    <LayoutLock>
      <p className="text-center text-5xl font-normal">
        <T _str="Lunch registration" />
      </p>

      {hasCardAuthentication ? (
        <>
          <ScanCardSvg />

          <p className="text-center text-5xl font-bold">
            <T _str="Scan your card" />
          </p>
        </>
      ) : (
        <>
          <TicketScanSvg />

          <p className="text-center text-5xl font-extrabold">
            <T _str="Scan your QR code on the scanner" />
          </p>
        </>
      )}
    </LayoutLock>
  );
};
